// Pure SVG-path geometry for the hand-rolled Key Dials renderer (DialView). Kept out of the
// component so the path maths can be unit-tested directly (mirrors how dial-renderer tests its
// own path output) — DialView renders these strings, it doesn't compute them inline.

// A span within this much of a full turn is treated as a complete ring. This matters when a
// scoped user can see only ONE section: the dial collapses to a single section (and possibly a
// single segment) spanning the whole circle. A naive single-arc path degenerates there because
// its start and end land on the same point — per the SVG spec an arc with coincident endpoints
// is dropped — so the "wedge" shrinks to a thin radial spoke and the curved label vanishes. The
// full-circle branches below draw a proper annulus / whole-ring baseline instead. The guard is
// tight (2π − ε) so every multi-section (N ≥ 2) path stays byte-for-byte on the original code.
const FULL_CIRCLE = 2 * Math.PI - 1e-9;

export function polar(cx: number, cy: number, r: number, a: number): [number, number] {
  return [cx + r * Math.cos(a), cy + r * Math.sin(a)];
}

/** A filled donut wedge between radii ri..ro spanning angles a0..a1. A full-circle span (a lone
 * scoped section, or its single segment) is drawn as a true annulus — an outer and an inner circle
 * wound in opposite directions so the non-zero fill leaves the centre hole — rather than the
 * degenerate spoke a single coincident-endpoint arc would collapse to. */
export function wedge(cx: number, cy: number, a0: number, a1: number, ri: number, ro: number): string {
  if (a1 - a0 >= FULL_CIRCLE) {
    // Split each circle into two ≤π arcs (SVG can't draw a full circle in one arc command) and
    // wind the outer clockwise, the inner counter-clockwise, so the annulus fills but the hole
    // stays empty under the default non-zero fill rule.
    const [o0x, o0y] = polar(cx, cy, ro, a0);
    const [omx, omy] = polar(cx, cy, ro, a0 + Math.PI);
    const [i0x, i0y] = polar(cx, cy, ri, a0);
    const [imx, imy] = polar(cx, cy, ri, a0 + Math.PI);
    return (
      `M ${o0x} ${o0y} A ${ro} ${ro} 0 0 1 ${omx} ${omy} A ${ro} ${ro} 0 0 1 ${o0x} ${o0y} Z ` +
      `M ${i0x} ${i0y} A ${ri} ${ri} 0 0 0 ${imx} ${imy} A ${ri} ${ri} 0 0 0 ${i0x} ${i0y} Z`
    );
  }
  const [x1, y1] = polar(cx, cy, ri, a0);
  const [x2, y2] = polar(cx, cy, ro, a0);
  const [x3, y3] = polar(cx, cy, ro, a1);
  const [x4, y4] = polar(cx, cy, ri, a1);
  const large = a1 - a0 > Math.PI ? 1 : 0;
  return `M ${x1} ${y1} L ${x2} ${y2} A ${ro} ${ro} 0 ${large} 1 ${x3} ${y3} L ${x4} ${y4} A ${ri} ${ri} 0 ${large} 0 ${x1} ${y1} Z`;
}

/** An open arc path for a curved text baseline; `reverse` flips it for the bottom half. A full-circle
 * span is split into two half-arcs so the baseline is the whole ring (centred on the section's
 * mid-angle at startOffset 50%) — a single coincident-endpoint arc would collapse and the label with
 * it. Orientation still honours `reverse`, keeping the text upright top or bottom. */
export function labelArc(cx: number, cy: number, a0: number, a1: number, r: number, reverse: boolean): string {
  if (a1 - a0 >= FULL_CIRCLE) {
    const mid = a0 + Math.PI; // (a0 + a1) / 2 — where startOffset 50% places the label
    const sweep = reverse ? 0 : 1;
    const [sx, sy] = polar(cx, cy, r, reverse ? a1 : a0);
    const [mx, my] = polar(cx, cy, r, mid);
    const [ex, ey] = polar(cx, cy, r, reverse ? a0 : a1);
    return `M ${sx} ${sy} A ${r} ${r} 0 0 ${sweep} ${mx} ${my} A ${r} ${r} 0 0 ${sweep} ${ex} ${ey}`;
  }
  const [x1, y1] = polar(cx, cy, r, reverse ? a1 : a0);
  const [x2, y2] = polar(cx, cy, r, reverse ? a0 : a1);
  const large = a1 - a0 > Math.PI ? 1 : 0;
  return `M ${x1} ${y1} A ${r} ${r} 0 ${large} ${reverse ? 0 : 1} ${x2} ${y2}`;
}
